import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Icon, TextInput, Typography } from '@qtm/react2';
import { diffLines } from 'diff';
import { AuthContext } from '../AuthContext';
import useAdminAccess from '../hooks/useAdminAccess';

const DOCS_URL = 'https://trustnest-docs.thalesdigital.io/docs/portal-internal-documentation/selfservice-overlay';

const defaultTemplate = () => ({
  id: 'new-item-id',
  title: 'New Self Service Action',
  description: '',
  api_id: '',
  operation_id: '',
  visibility: { type: 'all' },
  fields: [],
  draft: false,
});

export default function AdminSelfServicePage() {
  const { getToken } = useContext(AuthContext);
  const { isAdmin, adminLoading } = useAdminAccess();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeKey, setActiveKey] = useState('');
  const [editorValue, setEditorValue] = useState('');
  const [saveStatus, setSaveStatus] = useState('');
  const [saveError, setSaveError] = useState('');
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [apis, setApis] = useState([]);
  const [apisLoading, setApisLoading] = useState(false);
  const [apisError, setApisError] = useState('');
  const [apiFilter, setApiFilter] = useState('');
  const [selectedApi, setSelectedApi] = useState(null);
  const [operations, setOperations] = useState([]);
  const [opsLoading, setOpsLoading] = useState(false);
  const [opsError, setOpsError] = useState('');
  const [historyEntries, setHistoryEntries] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState('');
  const [compareState, setCompareState] = useState({ baselineId: '__current__', targetId: '' });

  const loadItems = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const token = await getToken();
      const resp = await fetch('/api/admin/selfservice/items', {
        headers: {
          Accept: 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
      if (!resp.ok) {
        const text = await resp.text().catch(() => '');
        throw new Error(text || `Failed to load items (${resp.status})`);
      }
      const data = await resp.json();
      setItems(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('AdminSelfServicePage: load failed', err);
      setItems([]);
      setError(err?.message || 'Unable to load self-service items.');
    } finally {
      setLoading(false);
    }
  }, [getToken]);

  const loadApis = useCallback(async () => {
    setApisLoading(true);
    setApisError('');
    try {
      const token = await getToken();
      const resp = await fetch('/api/apim/apis', {
        headers: {
          Accept: 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
      if (!resp.ok) {
        const text = await resp.text().catch(() => '');
        throw new Error(text || `Failed to load APIs (${resp.status})`);
      }
      const body = await resp.json();
      const items = Array.isArray(body?.value) ? body.value : Array.isArray(body) ? body : [];
      const mapped = items
        .map((item) => {
          const props = item?.properties || {};
          return {
            id: item?.id || item?.name || props.displayName || '',
            name: item?.name || '',
            displayName: props.displayName || item?.name || '',
            path: props.path || '',
          };
        })
        .filter((api) => api.name)
        .sort((a, b) => a.displayName.localeCompare(b.displayName));
      setApis(mapped);
    } catch (err) {
      console.error('AdminSelfServicePage: api catalog load failed', err);
      setApis([]);
      setApisError(err?.message || 'Unable to load APIs.');
    } finally {
      setApisLoading(false);
    }
  }, [getToken]);

  const loadHistory = useCallback(
    async (itemId) => {
      const target = typeof itemId === 'string' ? itemId.trim() : '';
      if (!target) {
        setHistoryEntries([]);
        setHistoryError('');
        return;
      }
      setHistoryLoading(true);
      setHistoryError('');
      try {
        const token = await getToken();
        const resp = await fetch(`/api/admin/selfservice/items/${encodeURIComponent(target)}/history`, {
          headers: {
            Accept: 'application/json',
            Authorization: `Bearer ${token}`,
          },
        });
        if (!resp.ok) {
          const text = await resp.text().catch(() => '');
          throw new Error(text || `Failed to load history (${resp.status})`);
        }
        const data = await resp.json();
        setHistoryEntries(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error('AdminSelfServicePage: history load failed', err);
        setHistoryEntries([]);
        setHistoryError(err?.message || 'Unable to load history.');
      } finally {
        setHistoryLoading(false);
      }
    },
    [getToken]
  );

  useEffect(() => {
    if (adminLoading) return;
    if (isAdmin) {
      loadItems();
      loadApis();
    } else {
      setLoading(false);
    }
  }, [adminLoading, isAdmin, loadItems, loadApis]);

  useEffect(() => {
    if (!activeKey || activeKey === '__new__') {
      return;
    }
    const match = items.find((item) => item.id === activeKey);
    if (match) {
      setEditorValue(JSON.stringify(match, null, 2));
    }
  }, [items, activeKey]);

  useEffect(() => {
    if (!activeKey || activeKey === '__new__') {
      setHistoryEntries([]);
      setHistoryError('');
      return;
    }
    loadHistory(activeKey);
  }, [activeKey, loadHistory]);

  useEffect(() => {
    setCompareState({ baselineId: '__current__', targetId: '' });
  }, [activeKey]);

  const selectedItemSummary = useMemo(() => {
    if (!activeKey || activeKey === '__new__') {
      return null;
    }
    return items.find((item) => item.id === activeKey) || null;
  }, [items, activeKey]);

  const historyEntryMap = useMemo(() => {
    const map = new Map();
    historyEntries.forEach((entry) => {
      if (entry?.id) {
        map.set(entry.id, entry);
      }
    });
    return map;
  }, [historyEntries]);

  const parsedEditorOverlay = useMemo(() => {
    try {
      const parsed = JSON.parse(editorValue);
      return parsed && typeof parsed === 'object' ? parsed : null;
    } catch (err) {
      return null;
    }
  }, [editorValue]);

  const baselineId = compareState.baselineId;
  const targetId = compareState.targetId;

  const baselineEntry = baselineId && baselineId !== '__current__' ? historyEntryMap.get(baselineId) : null;
  const baselineOverlay = baselineId === '__current__' ? parsedEditorOverlay : baselineEntry?.overlay || null;

  const targetEntry = targetId ? historyEntryMap.get(targetId) : null;
  const targetOverlay = targetEntry?.overlay || null;

  const baselineText = useMemo(() => {
    if (!baselineOverlay) {
      return '';
    }
    return stableStringify(prepareOverlayForDiff(baselineOverlay));
  }, [baselineOverlay]);

  const targetText = useMemo(() => {
    if (!targetOverlay) {
      return '';
    }
    return stableStringify(prepareOverlayForDiff(targetOverlay));
  }, [targetOverlay]);

  const diffParts = useMemo(() => {
    if (!baselineText || !targetText) {
      return [];
    }
    return diffLines(baselineText, targetText);
  }, [baselineText, targetText]);

  const diffSummary = useMemo(() => {
    if (!diffParts || diffParts.length === 0) {
      return { added: 0, removed: 0 };
    }
    let added = 0;
    let removed = 0;
    diffParts.forEach((part) => {
      const lines = part.value.split('\n').filter((line) => line.length > 0);
      if (part.added) {
        added += lines.length;
      } else if (part.removed) {
        removed += lines.length;
      }
    });
    return { added, removed };
  }, [diffParts]);

  const handleSelectItem = (item) => {
    setActiveKey(item.id);
    setEditorValue(JSON.stringify(item, null, 2));
    setSaveStatus('');
    setSaveError('');
  };

  const handleCreateNew = () => {
    const template = defaultTemplate();
    setActiveKey('__new__');
    setEditorValue(JSON.stringify(template, null, 2));
    setSaveStatus('');
    setSaveError('');
    setHistoryEntries([]);
    setHistoryError('');
  };

  const formatDateTime = useCallback((value) => {
    if (!value) {
      return '';
    }
    try {
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) {
        return String(value);
      }
      return date.toLocaleString();
    } catch (err) {
      return String(value);
    }
  }, []);

  const parseEditor = useCallback(() => {
    let payload;
    try {
      payload = JSON.parse(editorValue);
    } catch (err) {
      throw new Error(`Invalid JSON: ${err.message}`);
    }

    if (!payload || typeof payload !== 'object') {
      throw new Error('Editor content must be a JSON object.');
    }

    return payload;
  }, [editorValue]);

  const applyEditorPatch = useCallback((updates, successMessage) => {
    try {
      const payload = parseEditor();
      const next = { ...payload, ...updates };
      setEditorValue(JSON.stringify(next, null, 2));
      if (successMessage) {
        setSaveStatus(successMessage);
      }
      setSaveError('');
    } catch (err) {
      setSaveStatus('');
      setSaveError(err.message);
    }
  }, [parseEditor]);

  const handleSave = useCallback(async () => {
    setSaveStatus('');
    setSaveError('');
    let payload;
    try {
      payload = parseEditor();
    } catch (err) {
      setSaveError(err.message);
      return;
    }

    if (!payload || typeof payload.id !== 'string' || !payload.id.trim()) {
      setSaveError('The payload must include a non-empty "id" field.');
      return;
    }

    const targetId = payload.id.trim();
    payload.id = targetId;
    delete payload.editor_email;
    delete payload.editor_name;
    delete payload.editor_oid;
    delete payload.updated_at;

    setSaving(true);
    try {
      const token = await getToken();
      const resp = await fetch(`/api/admin/selfservice/items/${encodeURIComponent(targetId)}`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(payload),
      });
      if (!resp.ok) {
        const text = await resp.text().catch(() => '');
        throw new Error(text || `Save failed (${resp.status})`);
      }
      setSaveStatus('Item saved successfully.');
      setActiveKey(targetId);
      await loadItems();
      await loadHistory(targetId);
    } catch (err) {
      console.error('AdminSelfServicePage: save failed', err);
      setSaveError(err?.message || 'Failed to save item.');
    } finally {
      setSaving(false);
    }
  }, [parseEditor, getToken, loadItems, loadHistory]);

  const handleDelete = useCallback(async () => {
    if (!activeKey || activeKey === '__new__') {
      return;
    }
    const targetId = activeKey.trim();
    if (!targetId) {
      return;
    }
    if (typeof window !== 'undefined' && !window.confirm(`Delete item "${targetId}"?`)) {
      return;
    }

    setSaveStatus('');
    setSaveError('');
    setDeleting(true);
    try {
      const token = await getToken();
      const resp = await fetch(`/api/admin/selfservice/items/${encodeURIComponent(targetId)}`, {
        method: 'DELETE',
        headers: {
          Accept: 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
      if (!resp.ok) {
        const text = await resp.text().catch(() => '');
        throw new Error(text || `Delete failed (${resp.status})`);
      }
      setSaveStatus('Item deleted successfully.');
      setActiveKey('');
      setEditorValue('');
      setSelectedApi(null);
      setOperations([]);
      setHistoryEntries([]);
      setHistoryError('');
      await loadItems();
    } catch (err) {
      console.error('AdminSelfServicePage: delete failed', err);
      setSaveError(err?.message || 'Failed to delete item.');
    } finally {
      setDeleting(false);
    }
  }, [activeKey, getToken, loadItems]);

  const handleSelectBaseline = useCallback((id) => {
    if (!id) {
      return;
    }
    setCompareState((prev) => ({
      baselineId: id,
      targetId: id === prev.targetId ? '' : prev.targetId,
    }));
  }, []);

  const handleSetBaselineCurrent = useCallback(() => {
    setCompareState((prev) => ({
      baselineId: '__current__',
      targetId: prev.targetId === '__current__' ? '' : prev.targetId,
    }));
  }, []);

  const handleSelectTarget = useCallback((id) => {
    if (!id) {
      return;
    }
    setCompareState((prev) => ({
      baselineId: prev.baselineId,
      targetId: id,
    }));
  }, []);

  const handleResetCompare = useCallback(() => {
    setCompareState({ baselineId: '__current__', targetId: '' });
  }, []);

  const handleLoadHistoryOverlay = useCallback(
    (entry) => {
      if (!entry || !entry.overlay) {
        return;
      }
      try {
        const sanitized = sanitizeOverlayForEditor(entry.overlay);
        setEditorValue(JSON.stringify(sanitized, null, 2));
        setSaveStatus('Loaded history snapshot into editor. Save to persist changes.');
        setSaveError('');
        setCompareState({ baselineId: '__current__', targetId: '' });
      } catch (err) {
        console.error('AdminSelfServicePage: failed to load history snapshot', err);
        setSaveStatus('');
        setSaveError(err?.message || 'Unable to load history snapshot.');
      }
    },
    []
  );

  const filteredApis = useMemo(() => {
    const query = apiFilter.trim().toLowerCase();
    if (!query) {
      return apis;
    }
    return apis.filter((api) => {
      return [api.displayName, api.name, api.path]
        .filter(Boolean)
        .some((field) => field.toLowerCase().includes(query));
    });
  }, [apis, apiFilter]);

  const copyToClipboard = useCallback(async (value, label) => {
    if (!value) return;
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(value);
        setSaveStatus(`${label} copied to clipboard.`);
        setSaveError('');
      }
    } catch (err) {
      console.error('AdminSelfServicePage: clipboard copy failed', err);
      setSaveStatus('');
      setSaveError('Unable to copy to clipboard in this browser.');
    }
  }, []);

  const handleSelectApi = useCallback(async (api) => {
    if (!api || !api.name) {
      return;
    }
    setSelectedApi(api);
    setOpsLoading(true);
    setOpsError('');
    setOperations([]);
    try {
      const token = await getToken();
      const resp = await fetch(`/api/apim/apis/${encodeURIComponent(api.name)}`, {
        headers: {
          Accept: 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
      if (!resp.ok) {
        const text = await resp.text().catch(() => '');
        throw new Error(text || `Failed to load operations for ${api.name} (${resp.status})`);
      }
      const data = await resp.json();
      const ops = Array.isArray(data?.operations) ? data.operations : [];
      const mapped = ops
        .map((operation) => {
          const opProps = operation?.properties || {};
          return {
            id: operation?.name || opProps.operationId || '',
            operationId: opProps.operationId || operation?.name || '',
            displayName: opProps.displayName || operation?.name || '',
            method: opProps.method || opProps.verb || '',
            urlTemplate: opProps.urlTemplate || '',
          };
        })
        .sort((a, b) => a.displayName.localeCompare(b.displayName));
      setOperations(mapped);
    } catch (err) {
      console.error('AdminSelfServicePage: operations load failed', err);
      setOperations([]);
      setOpsError(err?.message || 'Unable to load operations.');
    } finally {
      setOpsLoading(false);
    }
  }, [getToken]);

  const handleOpenDocumentation = useCallback(() => {
    if (typeof window !== 'undefined') {
      window.open(DOCS_URL, '_blank', 'noopener,noreferrer');
    }
  }, []);

  if (adminLoading) {
    return (
      <div className="app-page-content" style={{ padding: 24 }}>
        <Typography>Checking admin permissions…</Typography>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="app-page-content" style={{ padding: 24 }}>
        <Alert severity="error">You need the admin role to manage self-service items.</Alert>
      </div>
    );
  }

  return (
    <div className="app-page-content" style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <Typography variant="heading-4">Self-Service Catalog (Admin)</Typography>
          <Typography variant="paragraph-small" style={{ opacity: 0.75 }}>
            Create or update self-service overlay items exposed to end users.
          </Typography>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <Button
            variant="outline"
            color="primary"
            type="button"
            onClickEvent={handleOpenDocumentation}
          >
            Documentation
          </Button>
          <Button variant="primary" leftIcon={<Icon icon="add" />} onClickEvent={handleCreateNew}>
            New item
          </Button>
        </div>
      </div>

      {error && <Alert severity="error" onClose={() => setError('')}>{error}</Alert>}
      {saveError && <Alert severity="error" onClose={() => setSaveError('')}>{saveError}</Alert>}
      {saveStatus && <Alert severity="success" onClose={() => setSaveStatus('')}>{saveStatus}</Alert>}

      <div style={{ display: 'grid', gridTemplateColumns: '280px 1fr', gap: 24, alignItems: 'start' }}>
        <aside style={{ border: '1px solid rgba(148,163,184,0.35)', borderRadius: 12, padding: 16 }}>
          <Typography variant="paragraph-small" style={{ fontWeight: 600, marginBottom: 12 }}>
            Existing items
          </Typography>
          {loading ? (
            <Typography>Loading…</Typography>
          ) : items.length === 0 ? (
            <Typography>No items yet.</Typography>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {items.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => handleSelectItem(item)}
                  style={{
                    textAlign: 'left',
                    padding: '10px 12px',
                    borderRadius: 10,
                    border: activeKey === item.id ? '2px solid rgba(59,130,246,0.7)' : '1px solid rgba(148,163,184,0.4)',
                    background: activeKey === item.id ? 'rgba(59,130,246,0.1)' : 'transparent',
                    cursor: 'pointer'
                  }}
                >
                  <div style={{ fontWeight: 600 }}>{item.title || item.id}</div>
                  <div style={{ fontSize: 12, opacity: 0.7 }}>{item.id}</div>
                </button>
              ))}
            </div>
          )}
        </aside>

        <section style={{ border: '1px solid rgba(148,163,184,0.35)', borderRadius: 12, padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
          <Typography variant="paragraph-small" style={{ fontWeight: 600 }}>
            Item definition
          </Typography>
          {selectedItemSummary && (
            <Typography variant="paragraph-small" style={{ opacity: 0.7 }}>
              Editing: {selectedItemSummary.title || selectedItemSummary.id}
            </Typography>
          )}
          {selectedItemSummary?.updated_at && (
            <Typography variant="paragraph-small" style={{ opacity: 0.65 }}>
              Last updated {formatDateTime(selectedItemSummary.updated_at)} by{' '}
              {selectedItemSummary.editor_name || selectedItemSummary.editor_email || 'unknown editor'}
            </Typography>
          )}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {selectedApi && (
              <Button
                variant="outline"
                size="small"
                onClickEvent={() => applyEditorPatch({ api_id: selectedApi.name }, `Applied api_id: ${selectedApi.name}`)}
              >
                Set api_id
              </Button>
            )}
            {selectedApi && operations.length > 0 && (
              <Button
                variant="outline"
                size="small"
                onClickEvent={() => applyEditorPatch({ operation_id: operations[0].operationId }, `Applied operation_id: ${operations[0].operationId}`)}
              >
                Use first operation
              </Button>
            )}
            <Button
              variant="outline"
              size="small"
              onClickEvent={() => {
                try {
                  const payload = parseEditor();
                  const next = { ...payload, fields: Array.isArray(payload.fields) ? payload.fields : [] };
                  setEditorValue(JSON.stringify(next, null, 2));
                  setSaveStatus('Ensured fields array exists.');
                  setSaveError('');
                } catch (err) {
                  setSaveStatus('');
                  setSaveError(err.message);
                }
              }}
            >
              Ensure fields array
            </Button>
          </div>
          <textarea
            value={editorValue}
            onChange={(event) => setEditorValue(event.target.value)}
            placeholder="Edit the JSON representation of the overlay item"
            style={{ width: '100%', minHeight: '360px', fontFamily: 'monospace', fontSize: 13, padding: 12, borderRadius: 8, border: '1px solid rgba(148,163,184,0.6)' }}
          />
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
            <Typography variant="paragraph-small" style={{ opacity: 0.7 }}>
              Tip: keep payload minimal; defaults can be enforced by the backend overlay logic.
            </Typography>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              {activeKey && activeKey !== '__new__' && (
                <Button
                  variant="outline"
                  onClickEvent={handleDelete}
                  disabled={deleting}
                  style={{ color: '#dc2626', borderColor: 'rgba(220,38,38,0.4)' }}
                >
                  {deleting ? 'Deleting…' : 'Delete'}
                </Button>
              )}
              <Button variant="primary" onClickEvent={handleSave} disabled={saving || deleting || !editorValue.trim()}>
                {saving ? 'Saving…' : 'Save'}
              </Button>
            </div>
          </div>
        </section>
      </div>

      <section style={{ border: '1px solid rgba(148,163,184,0.35)', borderRadius: 12, padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
          <Typography variant="paragraph-small" style={{ fontWeight: 600 }}>
            Change history
          </Typography>
          <Button
            variant="ghost"
            size="small"
            leftIcon={<Icon icon="refresh" />}
            onClickEvent={() => {
              if (activeKey && activeKey !== '__new__') {
                loadHistory(activeKey);
              }
            }}
            disabled={!activeKey || activeKey === '__new__' || historyLoading}
          >
            Refresh
          </Button>
        </div>
        {!activeKey || activeKey === '__new__' ? (
          <Typography variant="paragraph-small" style={{ opacity: 0.75 }}>
            Select an item to view its change history.
          </Typography>
        ) : historyError ? (
          <Alert severity="error" onClose={() => setHistoryError('')}>
            {historyError}
          </Alert>
        ) : historyLoading ? (
          <Typography variant="paragraph-small">Loading history…</Typography>
        ) : historyEntries.length === 0 ? (
          <Typography variant="paragraph-small" style={{ opacity: 0.75 }}>
            No changes recorded yet for this item.
          </Typography>
        ) : (
          <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 10 }}>
            {historyEntries.map((entry) => {
              const editorLabel = entry.editor_name || entry.editor_email || entry.editor_oid || 'Unknown user';
              const emailHint = entry.editor_email && entry.editor_email !== entry.editor_name ? entry.editor_email : '';
              const isBaseline = baselineId === entry.id;
              const isTarget = targetId === entry.id;
              const borderColor = isBaseline
                ? 'rgba(59,130,246,0.7)'
                : isTarget
                  ? 'rgba(16,185,129,0.7)'
                  : 'rgba(148,163,184,0.25)';
              const backgroundColor = isBaseline
                ? 'rgba(59,130,246,0.08)'
                : isTarget
                  ? 'rgba(16,185,129,0.12)'
                  : 'transparent';
              return (
                <li
                  key={entry.id}
                  style={{
                    border: `1px solid ${borderColor}`,
                    borderRadius: 10,
                    padding: '10px 12px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 4,
                    background: backgroundColor,
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12 }}>
                    <Typography variant="paragraph-small" style={{ fontWeight: 600 }}>
                      {formatDateTime(entry.timestamp)}
                    </Typography>
                    <Typography variant="paragraph-small" style={{ opacity: 0.75 }}>
                      {editorLabel}
                    </Typography>
                  </div>
                  {emailHint && (
                    <div style={{ fontFamily: 'monospace', fontSize: 12, opacity: 0.7 }}>{emailHint}</div>
                  )}
                  <div style={{ fontFamily: 'monospace', fontSize: 12, opacity: 0.6 }}>
                    {entry.item_id}
                  </div>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 6 }}>
                    <Button
                      variant="ghost"
                      size="small"
                      onClickEvent={() => handleSelectBaseline(entry.id)}
                    >
                      {isBaseline ? 'Baseline (A)' : 'Set as A'}
                    </Button>
                    <Button
                      variant="ghost"
                      size="small"
                      onClickEvent={() => handleSelectTarget(entry.id)}
                    >
                      {isTarget ? 'Compare (B)' : 'Set as B'}
                    </Button>
                    <Button
                      variant="ghost"
                      size="small"
                      onClickEvent={() => handleLoadHistoryOverlay(entry)}
                    >
                      Load snapshot
                    </Button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      <section style={{ border: '1px solid rgba(148,163,184,0.35)', borderRadius: 12, padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <Typography variant="paragraph-small" style={{ fontWeight: 600 }}>
              Compare versions
            </Typography>
            <Typography variant="paragraph-small" style={{ opacity: 0.7 }}>
              Select Baseline (A) and Comparison (B) snapshots to inspect differences. Use the current editor state as A to compare against historic versions.
            </Typography>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <Button
              variant="ghost"
              size="small"
              onClickEvent={() => handleSetBaselineCurrent()}
              disabled={baselineId === '__current__'}
            >
              Use current as A
            </Button>
            <Button
              variant="ghost"
              size="small"
              onClickEvent={handleResetCompare}
            >
              Reset
            </Button>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, alignItems: 'start' }}>
          <div style={{ background: 'rgba(59,130,246,0.05)', padding: 12, borderRadius: 8, border: baselineId === '__current__' ? '1px solid rgba(59,130,246,0.6)' : '1px solid rgba(148,163,184,0.35)' }}>
            <Typography variant="paragraph-small" style={{ fontWeight: 600, marginBottom: 4 }}>
              Baseline (A)
            </Typography>
            <Typography variant="paragraph-small" style={{ opacity: 0.75 }}>
              {baselineId === '__current__'
                ? 'Current editor state'
                : baselineEntry
                  ? `${formatDateTime(baselineEntry.timestamp)} — ${baselineEntry.editor_name || baselineEntry.editor_email || 'Unknown user'}`
                  : 'Not selected'}
            </Typography>
          </div>
          <div style={{ background: 'rgba(16,185,129,0.05)', padding: 12, borderRadius: 8, border: targetId ? '1px solid rgba(16,185,129,0.6)' : '1px solid rgba(148,163,184,0.35)' }}>
            <Typography variant="paragraph-small" style={{ fontWeight: 600, marginBottom: 4 }}>
              Comparison (B)
            </Typography>
            <Typography variant="paragraph-small" style={{ opacity: 0.75 }}>
              {targetEntry
                ? `${formatDateTime(targetEntry.timestamp)} — ${targetEntry.editor_name || targetEntry.editor_email || 'Unknown user'}`
                : 'Select a snapshot as B'}
            </Typography>
          </div>
        </div>

        <div style={{ border: '1px solid rgba(148,163,184,0.35)', borderRadius: 10, padding: 12, background: 'rgba(148,163,184,0.1)', display: 'flex', flexDirection: 'column', gap: 8 }}>
          {diffParts.length === 0 ? (
            <Typography variant="paragraph-small" style={{ opacity: 0.75 }}>
              Choose a baseline and comparison snapshot to see a line-by-line diff of the overlay JSON.
            </Typography>
          ) : (
            <>
              <Typography variant="paragraph-small" style={{ fontWeight: 600 }}>
                Differences: +{diffSummary.added} / -{diffSummary.removed}
              </Typography>
              <div style={{ maxHeight: 360, overflow: 'auto', border: '1px solid rgba(148,163,184,0.25)', borderRadius: 8, background: '#0f172a', color: '#e2e8f0', padding: 12 }}>
                {diffParts.map((part, index) => {
                  const prefix = part.added ? '+' : part.removed ? '-' : ' ';
                  const text = part.value.replace(/\n$/, '');
                  if (!text) {
                    return null;
                  }
                  const color = part.added ? '#4ade80' : part.removed ? '#f87171' : '#e2e8f0';
                  return (
                    <pre
                      key={`diff-${index}`}
                      style={{
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        margin: 0,
                        padding: '2px 4px',
                        background: part.added
                          ? 'rgba(34,197,94,0.12)'
                          : part.removed
                            ? 'rgba(239,68,68,0.12)'
                            : 'transparent',
                        borderRadius: 4,
                        color,
                        fontFamily: 'monospace',
                        fontSize: 12,
                      }}
                    >
                      {text
                        .split('\n')
                        .map((line, idx) => `${prefix}${line}`)
                        .join('\n')}
                    </pre>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </section>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
        <section style={{ border: '1px solid rgba(148,163,184,0.35)', borderRadius: 12, padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 12 }}>
            <Button variant="ghost" onClickEvent={loadApis} disabled={apisLoading} leftIcon={<Icon icon="refresh" />}>
              Refresh
            </Button>
          </div>
          <TextInput
            size="medium"
            placeholder="Filter by display name, name, or path"
            value={apiFilter}
            onChange={(event) => setApiFilter(event.target.value)}
            leftIcon={<Icon icon="search" />}
          />
          {apisError && <Alert severity="error" onClose={() => setApisError('')}>{apisError}</Alert>}
          <div style={{ maxHeight: 260, overflowY: 'auto', border: '1px solid rgba(148,163,184,0.25)', borderRadius: 10, padding: 12, display: 'flex', flexDirection: 'column', gap: 12 }}>
            {apisLoading ? (
              <Typography>Loading APIs…</Typography>
            ) : filteredApis.length === 0 ? (
              <Typography>No APIs match the current filter.</Typography>
            ) : (
              <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
                {filteredApis.map((api) => {
                  const isActive = selectedApi?.name === api.name;
                  return (
                    <li key={api.id}>
                      <button
                        type="button"
                        onClick={() => handleSelectApi(api)}
                        style={{
                          width: '100%',
                          textAlign: 'left',
                          border: isActive ? '2px solid rgba(59,130,246,0.7)' : '1px solid rgba(148,163,184,0.35)',
                          borderRadius: 10,
                          padding: '10px 12px',
                          background: isActive ? 'rgba(59,130,246,0.08)' : 'transparent',
                          cursor: 'pointer',
                        }}
                      >
                        <div style={{ fontWeight: 600 }}>{api.displayName}</div>
                        <div style={{ fontFamily: 'monospace', fontSize: 12 }}>
                          {api.name} — /{api.path}
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </section>

        <section style={{ border: '1px solid rgba(148,163,184,0.35)', borderRadius: 12, padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
          <Typography variant="paragraph-small" style={{ fontWeight: 600 }}>
            Operations
          </Typography>
          {selectedApi ? (
            <>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <Typography variant="paragraph-small" style={{ fontFamily: 'monospace' }}>
                  {selectedApi.name}
                </Typography>
                <Button
                  variant="ghost"
                  size="small"
                  onClickEvent={() => copyToClipboard(selectedApi.name, 'API name')}
                >
                  Copy API name
                </Button>
                {selectedApi.path && (
                  <Button
                    variant="ghost"
                    size="small"
                    onClickEvent={() => copyToClipboard(selectedApi.path, 'API path')}
                  >
                    Copy path
                  </Button>
                )}
              </div>
              {opsError && <Alert severity="error" onClose={() => setOpsError('')}>{opsError}</Alert>}
              {opsLoading ? (
                <Typography>Loading operations…</Typography>
              ) : operations.length === 0 ? (
                <Typography>No operations found for this API.</Typography>
              ) : (
                <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {operations.map((op) => (
                    <li
                      key={op.id}
                      style={{
                        lineHeight: 1.4,
                        border: '1px solid rgba(148,163,184,0.25)',
                        borderRadius: 8,
                        padding: 8,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 4
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div style={{ fontWeight: 600 }}>
                          {op.method && (
                            <span style={{ fontFamily: 'monospace', fontSize: 11, marginRight: 6 }}>{op.method}</span>
                          )}
                          {op.displayName}
                        </div>
                        <Button
                          variant="ghost"
                          size="small"
                          onClickEvent={() => applyEditorPatch({ operation_id: op.operationId }, `Applied operation_id: ${op.operationId}`)}
                        >
                          Set operation
                        </Button>
                      </div>
                      <div style={{ fontFamily: 'monospace', fontSize: 12 }}>
                        {op.operationId}
                      </div>
                      {op.urlTemplate && (
                        <div style={{ fontFamily: 'monospace', fontSize: 12, opacity: 0.75 }}>
                          {op.urlTemplate}
                        </div>
                      )}
                      <div style={{ display: 'flex', gap: 8 }}>
                        <Button
                          variant="ghost"
                          size="small"
                          onClickEvent={() => copyToClipboard(op.operationId, 'operation_id')}
                        >
                          Copy id
                        </Button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </>
          ) : (
            <Typography>Select an API to view operations.</Typography>
          )}
        </section>
      </div>
    </div>
  );
}

const OVERLAY_METADATA_KEYS = new Set(['editor_email', 'editor_name', 'editor_oid', 'updated_at']);

function sanitizeOverlayForEditor(value) {
  return cloneWithoutMetadata(value);
}

function prepareOverlayForDiff(value) {
  return normalizeForDiffValue(cloneWithoutMetadata(value));
}

function cloneWithoutMetadata(value) {
  if (Array.isArray(value)) {
    return value.map(cloneWithoutMetadata);
  }
  if (value && typeof value === 'object') {
    const result = {};
    Object.keys(value).forEach((key) => {
      if (OVERLAY_METADATA_KEYS.has(key)) {
        return;
      }
      result[key] = cloneWithoutMetadata(value[key]);
    });
    return result;
  }
  return value;
}

function normalizeForDiffValue(value) {
  if (Array.isArray(value)) {
    return value.map(normalizeForDiffValue);
  }
  if (value && typeof value === 'object') {
    const sortedKeys = Object.keys(value).sort((a, b) => a.localeCompare(b));
    const result = {};
    sortedKeys.forEach((key) => {
      result[key] = normalizeForDiffValue(value[key]);
    });
    return result;
  }
  return value;
}

function stableStringify(value) {
  return JSON.stringify(value, null, 2);
}
